if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          thumbnailList: '[id^="Slider-Thumbnails"]',
          mediaItems: ['.product__media-item'],
          thumbItems: ['.product__thumbs-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        // this.init();
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);

        // init() re-runs on every variant change, but by then filterMediaByVariant() may
        // have already removed non-matching items from the DOM. Capture the full media/thumb
        // list once so later variant changes can always restore previously-hidden items.
        if (this.allMediaItems) {
          this.elements.mediaItems = this.allMediaItems;
        } else {
          this.allMediaItems = this.elements.mediaItems;
        }
        if (this.allThumbItems) {
          this.elements.thumbItems = this.allThumbItems;
        } else {
          this.allThumbItems = this.elements.thumbItems;
        }

        this.optionNames = (this.dataset.optionNames || '').split('|').filter(Boolean);

        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
          this.initImageZoom(allMedia);
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);

        this.sliderOptions = {
          init: false,
          slidesPerView: '1',
          spaceBetween: mediaItemGap,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
            },
          },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
              breakpoints: {
                768: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                },
              },
            });
            break;
          case 'slider-freemode':
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              slidesPerView: 'auto',
            });
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom(items = []) {
        let dataSource = [];
        if (items.length > 0) {
          items.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
          preload: [1, 4],
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data }) => {
          if (data.src) {
            return data.src;
          }
          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
        });

        this.lightbox.on('destroy', () => {
          const { currIndex } = this.lightbox.pswp;

          if (this.sliderInstance && this.sliderInstance.slider) {
            this.sliderInstance.slider.slideToLoop(currIndex, 0, false);
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant || !variant.hasOwnProperty('id')) return;

        this.filterMediaByVariant(variant);

        if (!variant.hasOwnProperty('featured_media') || !variant.featured_media) return;

        if (this.sliderInstance.slider) {
          const slides = [...this.elements.mediaList.children];
          const slideIndex = slides.findIndex((media) => media.dataset.mediaId == variant.featured_media.id);
          if (slideIndex > -1) {
            this.sliderInstance.slider.slideToLoop(slideIndex, 0, false);
          }
        } else {
          this.scrollToActiveMedia(variant);
        }
      }

      // Strips whitespace/hyphens and lowercases, so option names/values that differ
      // only in spacing or casing still match - e.g. a '#Colour:LightBlue' tag matches
      // an option value of 'Light Blue', and a '#CableLength:2m' tag matches the option
      // name 'Cable Length'.
      normalizeTagText(value) {
        return String(value || '')
          .trim()
          .toLowerCase()
          .replace(/[\s-]+/g, '');
      }

      // Alt text can carry option-value tags like '#Colour:Blue #CableLength:2m'
      // (multiple values per key: '#Colour:Blue,Navy'), letting several images attach
      // to one colour and others to a cable length independent of any single variant's
      // one-image-per-variant featured image. Returns e.g. { colour: ['blue'] }.
      parseMediaTags(altText) {
        const tags = {};
        if (!altText) return tags;

        const regex = /#([a-z0-9 _-]+?):([^#]+)/gi;
        let match;
        while ((match = regex.exec(altText)) !== null) {
          const key = this.normalizeTagText(match[1]);
          const values = match[2]
            .split(',')
            .map((value) => this.normalizeTagText(value))
            .filter(Boolean);
          if (!key || values.length === 0) continue;
          tags[key] = (tags[key] || []).concat(values);
        }
        return tags;
      }

      // Maps this product's option names (in position order, from `data-option-names`)
      // to the given variant's option1/option2/option3 values, keyed the same way as
      // parseMediaTags() so the two can be compared directly.
      getSelectedOptionValues(variant) {
        const values = {};
        this.optionNames.forEach((name, index) => {
          values[this.normalizeTagText(name)] = this.normalizeTagText(variant[`option${index + 1}`]);
        });
        return values;
      }

      // Media tagged by option value (see parseMediaTags) is shown only when every
      // tagged option key matches the variant's selected value for that option (a key
      // with multiple tagged values matches any one of them). Untagged media falls back
      // to the legacy behavior: general (no `data-media-variants`) always stays visible,
      // media tied to variants is shown only for a matching variant. Falls back to
      // showing everything if a variant has no matching media, so the gallery never ends
      // up empty.
      isMediaVisibleForVariant(media, variant) {
        const tags = this.parseMediaTags(media.dataset.mediaTags);
        const tagKeys = Object.keys(tags);

        if (tagKeys.length > 0) {
          const selected = this.getSelectedOptionValues(variant);
          return tagKeys.every((key) => selected[key] !== undefined && tags[key].includes(selected[key]));
        }

        const variantIds = media.dataset.mediaVariants;
        return !variantIds || variantIds.split(',').includes(String(variant.id));
      }

      filterMediaByVariant(variant) {
        let visibleMedia = this.elements.mediaItems.filter((media) => this.isMediaVisibleForVariant(media, variant));
        if (visibleMedia.length === 0) visibleMedia = this.elements.mediaItems;
        visibleMedia = [...visibleMedia].sort((a, b) => a.dataset.mediaIndex - b.dataset.mediaIndex);

        this.elements.mediaList.innerHTML = '';
        visibleMedia.forEach((media) => this.elements.mediaList.appendChild(media));
        this.initImageZoom(visibleMedia);
        this.updateMediaCountClass(visibleMedia.length);
        this.updateFirstMediaSizes(visibleMedia.length);

        if (this.elements.thumbnailList && this.elements.thumbItems) {
          let visibleThumbs = this.elements.thumbItems.filter((thumb) => this.isMediaVisibleForVariant(thumb, variant));
          if (visibleThumbs.length === 0) visibleThumbs = this.elements.thumbItems;
          visibleThumbs = [...visibleThumbs].sort((a, b) => a.dataset.mediaPosition - b.dataset.mediaPosition);

          this.elements.thumbnailList.innerHTML = '';
          visibleThumbs.forEach((thumb) => this.elements.thumbnailList.appendChild(thumb));
        }

        if (this.sliderInstance.slider) this.sliderInstance.slider.update();
        if (this.thumbsInstance && this.thumbsInstance.slider) this.thumbsInstance.slider.update();
      }

      // Keeps the server-rendered `product--media-has-N`/`product--media-has-many` classes
      // (used by section-main-product.css for grid/column layout) in sync with the media
      // count actually visible after variant filtering, not the product's total media count.
      updateMediaCountClass(count) {
        const productElement = this.closest('.product');
        if (!productElement) return;

        [...productElement.classList].forEach((className) => {
          if (/^product--media-has-/.test(className)) {
            productElement.classList.remove(className);
          }
        });

        productElement.classList.add(`product--media-has-${count}`);
        if (count > 1) {
          productElement.classList.add('product--media-has-many');
        }
      }

      // In the 'columns' layout, custom.css stretches the first media item to
      // full width once variant filtering leaves 2-3 items visible (see the
      // 'Columns gallery' override). The `sizes` attribute driving that image's
      // srcset selection is baked in server-side from the product's *total*
      // media count, so it doesn't know a variant change can shrink the visible
      // count below 4 and make that image much wider than the server assumed -
      // it then keeps a low-res source and looks blurry. Recompute it here to
      // match what's actually on screen.
      updateFirstMediaSizes(count) {
        if (this.mediaLayout !== 'columns') return;

        const firstMedia = this.elements.mediaList && this.elements.mediaList.children[0];
        const img = firstMedia && firstMedia.querySelector('img');
        if (!img) return;

        const pageWidth = parseInt(this.dataset.pageWidth, 10);
        const mediaWidth = parseFloat(this.dataset.mediaWidth);
        if (!pageWidth || !mediaWidth) return;

        const columns = count > 1 ? (count <= 3 ? 1 : 2) : 1;
        const viewportCap = `${pageWidth + 30}px`;
        const desktopSize =
          columns > 1
            ? `calc(((min(${viewportCap}, 100vw) * ${mediaWidth}) - 4.5rem - ${columns - 1}rem) / ${columns})`
            : `calc(((min(${viewportCap}, 100vw) * ${mediaWidth}) - 4.5rem) / ${columns})`;

        img.sizes = `(max-width: 768px) calc(100vw - 3rem), ${desktopSize}`;
      }

      scrollToActiveMedia(variant) {
        if (FoxTheme.config.mqlMobile) return;

        const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
        if (selectedMedia) {
          window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
        }
      }
    }
  );
}
